import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Accordion from '../../components/accordion/Accordion';
import Modal from '../../components/modal/Modal';
import MediaLibrary from '../mediaLibrary/MediaLibrary';
import { TPageViewModel } from '../../services/models/PageViewModel';
import { IPropertyGroupSound } from '../../services/models/PropertyViewModel';
import { useStore } from '../../store/store';
import { AddStyle } from '../properties/placeholderProperties/PlaceholderProperties';
import { DynamicAudioField } from '../../components/properties/Properties';
import Divider from '@mui/material/Divider/Divider';
import Button from '@mui/material/Button/Button';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography/Typography';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AiVoiceShell from '../../components/aiVoice/AiVoiceShell';
import WikiHelpTooltip from '../../components/wiki/WikiHelpTooltip';
import { canonicalizeGoogleDraft } from '../../components/aiVoice/google/utils/canonicalizeGoogleDraft';
import { gunzipToUtf8, readTtsMetadataFromMp3Url } from '../../utils/mp3Metadata';

interface IPageSoundPropertyProps {
  audio: IPropertyGroupSound;
  page?: TPageViewModel;
  accordion?: boolean;
}
interface IWrapperProps {
  wrapper: boolean;
  children: React.ReactNode;
}

const isAiVoiceAudioFile = (aName: string) => {
  return aName.toLowerCase().includes('aivoice') || aName.toLowerCase().includes('\\aivoice\\');
};

type AudioWarningState = 'none' | 'outdated' | 'wrong-origin';

export default observer(function PageSoundProperty({ audio, accordion = true }: IPageSoundPropertyProps) {
  const { mediaLibraryStore, propertyStore, pageStore, courseStore } = useStore();
  const { setDesignProperty } = propertyStore;
  const { openMediaLibraryFromEditor } = mediaLibraryStore;
  const theme = useTheme();
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [isAIVoiceOpen, setIsAIVoiceOpen] = useState(false);
  const [defaultOpen, setDefaultOpen] = useState(false);
  const [fileExist, setFileExist] = useState(!!audio.items.URL.option.value); // Ensure proper initial state
  const [isAdded, setIsAdded] = useState(audio.items.URL.option.value.trim() !== '');
  const [audioWarningState, setAudioWarningState] = useState<AudioWarningState>('none');

  const handleSelectAudio = (aSrc: string, aFileName?: string) => {
    setIsMediaLibraryOpen(false);
    setIsAIVoiceOpen(false);
    setFileExist(true); // Update state immediately
    runInAction(() => {
      if (aFileName !== undefined) {
        audio.items.name.option.value = aFileName;
        setDesignProperty(audio.items.name, aFileName);
      }
      audio.items.URL.option.value = aSrc;
      setDesignProperty(audio.items.URL, aSrc);
    });
  };

  const handleAddAudio = () => {
    openMediaLibraryFromEditor();
    setIsMediaLibraryOpen(true);
    setDefaultOpen(true);
  };

  const handleDeleteAudio = () => {
    setIsAdded(false);
    setFileExist(false);
    runInAction(() => {
      audio.items.URL.option.value = ''; // Clear the audio URL
      setDesignProperty(audio.items.URL, '');
    });
  };

  useEffect(() => {
    setFileExist(!!audio.items.URL.option.value);
  });

  const isAiVoiceAudio = isAiVoiceAudioFile(audio.items.name.option.value);

  useEffect(() => {
    const audioUrl = audio.items.URL.option.value;
    const courseID = pageStore.coursePage?.courseID ?? courseStore.pageProperties?.courseID;
    const pageID = pageStore.coursePage?.coursePageID ?? courseStore.pageProperties?.coursePageID;

    setAudioWarningState('none');

    if (!audioUrl || courseID === undefined || pageID === undefined) {
      return;
    }

    const abortController = new AbortController();
    const compare = async () => {
      try {
        const metadata = await readTtsMetadataFromMp3Url(audioUrl, abortController.signal);
        if (abortController.signal.aborted || !metadata.canonicalGzipPayload) return;

        const currentCourseID = String(courseID);
        const currentPageID = String(pageID);

        if (metadata.origin && (metadata.origin.courseID !== currentCourseID || metadata.origin.pageID !== currentPageID)) {
          setAudioWarningState('wrong-origin');
          return;
        }

        const canonicalFromMetadata = await gunzipToUtf8(metadata.canonicalGzipPayload);
        if (abortController.signal.aborted) return;

        const storedDraft = await propertyStore.getGoogleAIVoiceDraftByIds(courseID, pageID);
        if (abortController.signal.aborted) return;

        const hasText = storedDraft.segments.some((segment) => segment.text.trim().length > 0);
        if (!hasText) return;

        const canonicalFromStoredDraft = canonicalizeGoogleDraft(storedDraft);
        setAudioWarningState(canonicalFromStoredDraft !== canonicalFromMetadata ? 'outdated' : 'none');
      } catch {
        // Indeterminate state should render nothing.
      }
    };

    compare();

    return () => {
      abortController.abort();
    };
  }, [audio.items.URL.option.value, audio.items.ttsPrompt.option.value, courseStore.pageProperties?.courseID, courseStore.pageProperties?.coursePageID, pageStore.coursePage?.courseID, pageStore.coursePage?.coursePageID, propertyStore]);

  const Wrapper = ({ wrapper, children }: IWrapperProps) => {
    if (wrapper) {
      return (
        <Accordion title={'Audio'} isOpen={defaultOpen}>
          {children}
        </Accordion>
      );
    }
    return <>{children}</>;
  };

  if (!isAdded) {
    return (
      <>
        <Divider />
        <WikiHelpTooltip tooltipId='BackgroundAudio'>
          <div>
            <AddStyle
              styleName={'Audio'}
              addStyle={() => {
                setIsAdded(true);
                setDefaultOpen(true);
              }}
            />
          </div>
        </WikiHelpTooltip>
      </>
    );
  }

  return (
    <>
      <Accordion title={'Audio'} isOpen={defaultOpen}>
        {fileExist ? (
          <>
            {audioWarningState !== 'none' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5), paddingLeft: theme.spacing(1), paddingBottom: theme.spacing(0.5), color: theme.palette.warning.main }}>
                <WarningAmberRoundedIcon fontSize='small' sx={{ color: theme.palette.warning.main }} />
                <Typography variant='caption' sx={{ color: theme.palette.warning.main }}>
                  {audioWarningState === 'wrong-origin' ? 'This audio was generated for a different page/course.' : 'Latest AI Voice script changes are not yet generated into audio.'}
                </Typography>
              </div>
            )}
            <DynamicAudioField
              name={audio.items.name}
              url={audio.items.URL}
              autoPlay={audio.items.autoPlay}
              loop={audio.items.loop}
              volume={audio.items.volume}
              onReplace={handleAddAudio}
              onDelete={handleDeleteAudio}
              showOpenEditor={isAiVoiceAudio}
              onOpenEditor={() => setIsAIVoiceOpen(true)}
            />
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing(1), width: '100%' }}>
            <WikiHelpTooltip tooltipId='BackgroundAudio'>
              <div style={{ width: 'min-content' }}>
                <Button color='dark' variant='contained' style={{ alignSelf: 'baseline', minWidth: 200 }} onClick={handleAddAudio}>
                  Select audio file
                </Button>
              </div>
            </WikiHelpTooltip>
            <Typography variant='body1'>or</Typography>
            <div style={{ width: 'min-content' }}>
              <Button color='dark' variant='contained' style={{ alignSelf: 'baseline', minWidth: 200 }} onClick={() => setIsAIVoiceOpen(true)}>
                GENERATE AI VOICE
              </Button>
            </div>
          </div>
        )}
      </Accordion>

      <Modal
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        title='Media Library'
        content={<MediaLibrary onSelect={handleSelectAudio} acceptedFiletypes={['.mp3', '.wav', '.ogg', '.m4a']} />}
      />
      <Modal isOpen={isAIVoiceOpen} onClose={() => setIsAIVoiceOpen(false)} title='AI Voice Generator' content={<AiVoiceShell onSelect={handleSelectAudio} />} />
      <Divider />
    </>
  );
});
